import React from 'react';
import BulkFieldCheckbox from '../../changeAll/BulkFieldCheckbox';
import { useChangeAll } from '../../changeAll/ChangeAllContext';
import { FieldMenu } from '../../../../components/fieldmenu/FieldMenu';
import { useFieldHistory } from '../../../../components/fieldmenu/FieldHistoryContext';
import { resolveHistoryFields } from '../../../../components/fieldmenu/resolveHistoryFields';
import {
  findMaintenanceTarget,
  maintenanceUrl,
} from '../../../../components/fieldmenu/fieldMaintenanceTargets';
import { useTryPermissions } from '../../../../contexts/PermissionContext';

export type Highlight = 'yellow' | 'red' | 'green' | null | undefined;

type LabelProps = {
  children: React.ReactNode;
  /** Label box width, given in px at the 16px baseline (default 88, matching the
   *  prototype's LABEL_W). Converted to rem internally so the box grows with the
   *  root type scale — a px width would clip scaled-up label text. */
  width?: number;
  /** Optional highlight background. */
  highlight?: Highlight;
  className?: string;
  /** When set and change-all mode is enabled, renders an "apply to all pros"
   * checkbox before the label text. No effect when change-all mode is off. */
  bulkFieldName?: string;
  /** May the user edit this field on this record? Pass the SAME expression that
   * chooses between the input and a ReadValue — the checkbox is a write affordance
   * (ticking an untouched field fans the anchor's value), so it has to answer to the
   * field's own permission. Defaults true for fields nothing gates. */
  bulkEditable?: boolean;
  /** camelCase field key(s) for the right-click "View History" menu, for
   * compound fields (e.g. Pool/Chass) where bulkFieldName doesn't apply.
   * Defaults to [bulkFieldName] when omitted. No effect outside a
   * FieldHistoryProvider. */
  historyFields?: string[];
  /** Current field value (already display-formatted); enables "Copy Value"
   * on the right-click menu. No effect outside a FieldHistoryProvider. */
  value?: string;
};

// `of-label` is a no-op hook class (no styles of its own) that lets a host
// scope an override — e.g. the quote drawer left-aligns all labels via
// `.qled-inter .of-label`.
// Sizes are rem, not px: 0.71875rem == 11.5px at the 16px browser default, but it
// tracks --app-font-size instead of freezing. Any px text size here is invisible to
// the root type scale.
const baseLabel =
  'of-label text-[0.71875rem] font-bold text-cmp-field-label ' +
  'flex-shrink-0 whitespace-nowrap text-right';

/**
 * Static field label (right-aligned, fixed width). Used inline to the left
 * of an input. Mirrors `primitiveStyles.label` from the prototype.
 */
export const Label: React.FC<LabelProps> = ({
  children,
  width = 88,
  highlight,
  className,
  bulkFieldName,
  bulkEditable = true,
  historyFields,
  value,
}) => {
  const { enabled } = useChangeAll();
  const fieldHistory = useFieldHistory();
  const permissions = useTryPermissions();
  const hl =
    highlight === 'yellow'
      ? 'bg-yellow-50 dark:bg-yellow-900/30 text-slate-900 dark:text-gray-100 px-1.5 rounded-sm'
      : highlight === 'red'
        ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 font-bold px-1.5 rounded-sm'
        : '';

  // camelCase keys — the maintenance map is keyed by those, unlike FieldMenu's
  // history colnames. Only resolvable inside a provider, which is where the
  // host form supplies the value the table search is seeded with.
  const fieldKeys = historyFields ?? (bulkFieldName ? [bulkFieldName] : []);
  const target = fieldHistory?.getFieldValue ? findMaintenanceTarget(fieldKeys) : undefined;
  // No permission context (quote drawer, tests) means no route gating to apply.
  const maintenance =
    target && permissions?.hasAccess(target.route) !== false
      ? {
          label: target.label,
          url: maintenanceUrl(target.route, fieldHistory!.getFieldValue!(target.queryField)),
        }
      : undefined;

  const content = fieldHistory ? (
    <FieldMenu
      historyFields={resolveHistoryFields(fieldKeys)}
      value={value}
      maintenance={maintenance}
    >
      {children}
    </FieldMenu>
  ) : (
    children
  );

  if (enabled && bulkFieldName) {
    return (
      <span
        className={`inline-flex items-center gap-1 ${baseLabel} ${hl} ${className ?? ''}`}
        style={{ width: `${width / 16}rem` }}
      >
        <BulkFieldCheckbox fieldName={bulkFieldName} editable={bulkEditable} />
        {content}
      </span>
    );
  }
  return (
    <span
      className={`${baseLabel} ${hl} ${className ?? ''}`}
      style={{ width: `${width / 16}rem` }}
    >
      {content}
    </span>
  );
};

type LabelStackedProps = {
  children: React.ReactNode;
  highlight?: Highlight;
  className?: string;
};

/**
 * Block-level uppercase label used above a stacked input. Mirrors
 * `primitiveStyles.labelStacked` from the prototype.
 */
export const LabelStacked: React.FC<LabelStackedProps> = ({
  children,
  highlight,
  className,
}) => {
  const hl =
    highlight === 'yellow'
      ? 'bg-yellow-50 dark:bg-yellow-900/30'
      : highlight === 'red'
        ? 'text-red-800 dark:text-red-200'
        : 'text-slate-500 dark:text-gray-400';
  return (
    <span
      className={
        `text-[0.65625rem] font-medium uppercase tracking-wider ${hl} ${className ?? ''}`
      }
    >
      {children}
    </span>
  );
};

export default Label;
